import React, { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  Box, 
  Stack, 
  Typography, 
  IconButton, 
  ToggleButton, 
  ToggleButtonGroup,
  Modal,
  Button
} from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer
} from 'recharts'

import { trackEvent } from 'analytics'

import { useSsidStats } from '../../use-ssid-stats'
import { useDetail } from '../../use-event-ssids'
import { ActionIcon } from 'components/Modal/ActionModal'
import { getEventStatus } from '../../helpers'
import { ConsumptionTooltip, DevicesTooltip } from '.'

const formatBytes = (bytes: number) => {
  if (bytes === 0) return { value: '0', unit: 'octet' }
  const k = 1024
  const sizes = ['octets', 'Ko', 'Mo', 'Go', 'To']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return {
    value: (Math.round((bytes / Math.pow(k, i)) * 100) / 100).toString(),
    unit: sizes[i]
  }
}

const formatBytesForChart = (bytes: number) => {
  const { value, unit } = formatBytes(bytes)
  return `${value} ${unit}`
}

const StatCard = ({ value, label }) => {
  const isBytes = typeof value === 'object' && value.unit

  return (
    <Box
      sx={{
        flex: 1,
        border: '1px solid #EFEFEF',
        borderRadius: '12px',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70px'
      }}
    >
      {isBytes ? (
        <Stack direction="row" alignItems="baseline" gap={0.5}>
          <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#464646' }}>
            {value.value}
          </Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 400, color: '#989898', mb: 0.3 }}>
            {value.unit}
          </Typography>
        </Stack>
      ) : (
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#464646' }}>
          {value}
        </Typography>
      )}
      <Typography sx={{ fontSize: 9, color: '#656565', textAlign: 'center', mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  )
}

interface GraphCardProps {
  title: string
  subtitle?: string
  data: Array<{ date: string; value: number }>
  color: string
  onExpand?: () => void
  formatValue?: (value: number) => string
  isUpcoming?: boolean
  isActive?: boolean
  scale?: 'day' | 'month'
  onScaleChange?: (scale: 'day' | 'month') => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipComponent: React.ComponentType<any>
}

const GraphCard: React.FC<GraphCardProps> = ({ 
  title, 
  subtitle,
  data, 
  color, 
  onExpand, 
  formatValue = (v) => v.toString(),
  isUpcoming = false,
  isActive = false,
  scale = 'day',
  onScaleChange,
  tooltipComponent: TooltipComponent
}) => {
  const { t } = useTranslation()

  if (isUpcoming) {
    return (
      <Box sx={{ pb: 2, width: '100%' }}>
        <Stack direction="row" gap={0.5} alignItems="baseline" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#464646' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 10, fontWeight: 400, color: '#989898' }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        <Box 
          sx={{ 
            width: '100%', 
            height: 150, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            border: '1px solid #EFEFEF',
            borderRadius: '12px',
            backgroundColor: '#F7F7F7'
          }}
        >
          <Typography 
            sx={{ 
              fontSize: 11, 
              color: '#989898', 
              textAlign: 'center', 
              px: 2,
              maxWidth: '270px',
              lineHeight: 1.4
            }}
          >
            {t('event_ssids.stats.upcoming_message')}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ pb: 2, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Stack direction="row" gap={0.5} alignItems="baseline">
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#464646' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 10, fontWeight: 400, color: '#989898' }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          {onScaleChange && !isActive && (
            <ToggleButtonGroup
              value={scale}
              exclusive
              onChange={(e, newScale) => {
                if (newScale !== null) {
                  onScaleChange(newScale)
                }
              }}
              size="small"
              sx={{
                height: 24,
                '& .MuiToggleButton-root': {
                  fontSize: 9,
                  px: 1,
                  py: 0,
                  textTransform: 'none',
                  border: '1px solid #EFEFEF',
                  color: '#656565',
                  '&.Mui-selected': {
                    backgroundColor: '#E8E8E8',
                    color: '#464646',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#DEDEDE'
                    }
                  },
                  '&:hover': {
                    backgroundColor: '#F7F7F7'
                  }
                }
              }}
            >
              <ToggleButton value="day">{t('event_ssids.stats.scale.day')}</ToggleButton>
              <ToggleButton value="month">{t('event_ssids.stats.scale.month')}</ToggleButton>
            </ToggleButtonGroup>
          )}
          {data.length > 0 && (
            <IconButton 
              onClick={onExpand}
              sx={{
                width: 28,
                height: 28,
                fontSize: 12,
                borderRadius: '50%',
                backgroundColor: '#F7F7F7',
                color: '#656565',
                '&:hover': {
                  backgroundColor: '#EFEFEF',
                },
              }}
            >
              <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {data.length === 0 ? (
        <Box 
          sx={{ 
            width: '100%', 
            height: 150, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            border: '1px solid #EFEFEF',
            borderRadius: '12px'
          }}
        >
          <Typography sx={{ fontSize: 11, color: '#989898' }}>
            {t('event_ssids.stats.no_data')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: 150, ml: -2, mr: -1, mt: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 9, fill: '#A2AAB8' }}
              />
              <YAxis 
                tick={{ fontSize: 9, fill: '#A2AAB8' }}
                width={60}
                tickFormatter={formatValue}
              />
              <RechartsTooltip content={<TooltipComponent scale={scale} />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={color} 
                strokeWidth={2}
                dot={data.length === 1 || scale === 'day' ? { r: 4, fill: color } : false}
                activeDot={scale === 'day' ? { r: 6, fill: color } : false}
                isAnimationActive={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  )
}

const StatsTabSkeleton: React.FC = () => {
  return (
    <Stack gap={2.5}>
      <Stack direction="row" gap={1.5} sx={{ width: '100%' }}>
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              border: '1px solid #EFEFEF',
              borderRadius: '12px',
              p: 1.5,
              minHeight: '70px',
              backgroundColor: '#F7F7F7'
            }}
          />
        ))}
      </Stack>

      {[1, 2].map((i) => (
        <Box key={i} sx={{ pb: 2, width: '100%' }}>
          <Box 
            sx={{ 
              width: '150px', 
              height: '16px', 
              backgroundColor: '#F7F7F7',
              borderRadius: '4px',
              mb: 1.5
            }} 
          />
          <Box 
            sx={{ 
              width: '100%', 
              height: 150,
              backgroundColor: '#F7F7F7',
              borderRadius: '12px'
            }} 
          />
        </Box>
      ))}
    </Stack>
  )
}

type Scale = 'day' | 'month'

type ExpandedGraph = {
  type: string
  title: string
  subtitle?: string
  color: string
  data: Array<{ date: string; value: number }>
  formatValue?: (value: number) => string
  scale: Scale
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipComponent: React.ComponentType<any>
}

const StatsTab: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [scale, setScale] = useState<Scale>('day')
  const { item } = useDetail()
  const isActive = item ? getEventStatus(item).isActive : false
  const { loading, stats } = useSsidStats(id as string, scale)
  const [expandedGraph, setExpandedGraph] = useState<ExpandedGraph | null>(null)

  const handleScaleChange = (newScale: Scale) => {
    setScale(newScale)
    trackEvent('wifi_management_stats_scale_changed', { scale: newScale })
  }

  const handleExpand = (graph: ExpandedGraph) => {
    setExpandedGraph(graph)
    trackEvent('wifi_management_stats_expanded', { graph_type: graph.type, scale: graph.scale })
  }

  const handleCloseModal = () => {
    setExpandedGraph(null)
  }

  const consumptionData = useMemo(() => stats?.consumption_graph || [], [stats])
  const devicesData = useMemo(() => stats?.devices_graph || [], [stats])

  if (loading) {
    return (
      <Box
        sx={{
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <StatsTabSkeleton />
      </Box>
    )
  }

  const isUpcoming = stats?.is_upcoming || false

  return (
    <>
      <Box
        sx={{
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <Stack gap={2.5}>
          <Stack direction="row" gap={1.5} sx={{ width: '100%' }}>
            <StatCard 
              value={isUpcoming ? '0' : (stats?.connected_devices || 0).toString()} 
              label={t('event_ssids.stats.connected_devices')}
            />
            <StatCard 
              value={isUpcoming ? { value: '0', unit: 'octet' } : formatBytes(stats?.total_consumption || 0)} 
              label={t('event_ssids.stats.total_consumption')}
            />
            <StatCard 
              value={isUpcoming ? { value: '0', unit: 'octet' } : formatBytes(stats?.avg_consumption_per_device || 0)} 
              label={t('event_ssids.stats.avg_per_device')}
            />
          </Stack>

          <GraphCard
            title={t('event_ssids.stats.consumption_title')}
            subtitle={t('event_ssids.stats.consumption_unit')}
            data={consumptionData}
            color="#FDAE61"
            formatValue={formatBytesForChart}
            isUpcoming={isUpcoming}
            isActive={isActive}
            scale={scale}
            onScaleChange={handleScaleChange}
            tooltipComponent={ConsumptionTooltip}
            onExpand={() => handleExpand({ 
              type: 'consumption', 
              title: t('event_ssids.stats.consumption_title'),
              subtitle: t('event_ssids.stats.consumption_unit'),
              color: '#FDAE61',
              data: consumptionData,
              formatValue: formatBytesForChart,
              scale,
              tooltipComponent: ConsumptionTooltip
            })}
          />

          <GraphCard
            title={t('event_ssids.stats.devices_over_time')}
            data={devicesData}
            color="#2570EB"
            isUpcoming={isUpcoming}
            isActive={isActive}
            scale={scale}
            onScaleChange={handleScaleChange}
            tooltipComponent={DevicesTooltip}
            onExpand={() => handleExpand({ 
              type: 'devices', 
              title: t('event_ssids.stats.devices_over_time'),
              color: '#2570EB',
              data: devicesData,
              scale,
              tooltipComponent: DevicesTooltip
            })}
          />
        </Stack>
      </Box>

      <Modal
        open={!!expandedGraph}
        onClose={handleCloseModal}
        aria-labelledby="graph-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 900,
          height: 580,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <ActionIcon>
                <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
              </ActionIcon>
              <Stack direction="row" gap={0.5} alignItems="baseline">
                <Typography id="graph-modal-title" variant="h6" component="h2">
                  {expandedGraph?.title}
                </Typography>
                {expandedGraph?.subtitle && (
                  <Typography sx={{ fontSize: 12, color: '#989898' }}>
                    {expandedGraph.subtitle}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Button
              onClick={handleCloseModal}
              sx={{
                minWidth: 28,
                minHeight: 28,
                padding: 0,
                borderRadius: '50%',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: '#F0F0F0',
                }
              }}
            >
              <i className="fa-regular fa-xmark" style={{ width: '14px' }} />
            </Button>
          </Box>

          {expandedGraph && (
            <ResponsiveContainer width="100%" height={480}>
              <LineChart data={expandedGraph.data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickFormatter={expandedGraph.formatValue || ((v) => v.toString())}
                />
                <RechartsTooltip content={<expandedGraph.tooltipComponent scale={expandedGraph.scale} />} />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke={expandedGraph.color} 
                  strokeWidth={3}
                  dot={expandedGraph.data.length === 1 || expandedGraph.scale === 'day' ? { r: 5, fill: expandedGraph.color } : false}
                  activeDot={expandedGraph.scale === 'day' ? { r: 8, fill: expandedGraph.color } : false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Modal>
    </>
  )
}

export default StatsTab